import { useState, useEffect, useMemo } from 'react'
import { Section, SectionTitle, Grid, Card, CardTitle, CardText, CardNumber, CopyButton, SourceLink, ModalOverlay, ModalContent, CloseModalButton, ModalImage, LoadMoreButton, PremiumCard, Tag, CardImage } from './styles'
import { prompts } from '../data/prompts'
import { FaRegCopy, FaTimes, FaExternalLinkAlt } from 'react-icons/fa'

export default function PromptSection() {
    const [selectedPrompt, setSelectedPrompt] = useState(null)
    const [copied, setCopied] = useState(false)
    const [imgLoaded, setImgLoaded] = useState(false)
    const [visibleCount, setVisibleCount] = useState(24)

    // Randomize prompts on mount
    const shuffledPrompts = useMemo(() => {
        return [...prompts].sort(() => Math.random() - 0.5)
    }, [])

    const handleCardClick = (prompt) => {
        setSelectedPrompt(prompt)
        setCopied(false)
        setImgLoaded(false)
    }

    const handleCloseModal = () => {
        setSelectedPrompt(null)
        setImgLoaded(false)
    }

    const handleLoadMore = () => {
        setVisibleCount(prev => prev + 15)
    }

    const handleCopy = () => {
        if (selectedPrompt) {
            navigator.clipboard.writeText(selectedPrompt.prompt)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        }
    }

    // Stop scroll when modal is open
    useEffect(() => {
        if (selectedPrompt) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = 'unset'
        }
    }, [selectedPrompt])

    return (
        <>
            <Section>
                <SectionTitle>Top 100+ Prompts</SectionTitle>
                <Grid>
                    {shuffledPrompts.slice(0, visibleCount).map((item, index) => {
                        const isPremiumPosition = index === 5; // Insert premium card after 5th card
                        return (
                            <>
                                {isPremiumPosition && (
                                    <PremiumCard key="premium" onClick={() => alert('이 프롬프트는 커뮤니티 전용입니다! 아래 Prompt Engineering 섹션에서 카카오톡 커뮤니티에 참여하시면 특별 프롬프트 10종을 즉시 확인하실 수 있습니다.')} style={{ padding: '1rem', aspectRatio: '1', display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'center' }}>
                                        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🔒</div>
                                        <CardTitle style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>Unlock 10+ Secret Prompts</CardTitle>
                                        <CardText style={{ fontSize: '0.8rem' }}>Community Only</CardText>
                                    </PremiumCard>
                                )}
                                <Card key={item.id} onClick={() => handleCardClick(item)} style={{ padding: '1rem' }}>
                                    <div style={{ position: 'relative', width: '100%', aspectRatio: '1', overflow: 'hidden', borderRadius: '12px' }}>
                                        <CardImage
                                            src={`/prompts/${item.id}.png`}
                                            alt={item.title}
                                            onError={(e) => {
                                                if (e.target.src.includes('.png')) {
                                                    e.target.src = `/prompts/${item.id}.jpg`;
                                                } else if (e.target.src.includes('.jpg')) {
                                                    e.target.src = `/prompts/${item.id}.jpeg`;
                                                } else {
                                                    e.target.style.display = 'none';
                                                }
                                            }}
                                        />
                                        <div style={{
                                            position: 'absolute',
                                            top: '10px',
                                            left: '10px',
                                            background: 'rgba(0,0,0,0.5)',
                                            color: 'white',
                                            padding: '2px 8px',
                                            borderRadius: '6px',
                                            fontSize: '0.8rem',
                                            fontWeight: '600',
                                            backdropFilter: 'blur(4px)'
                                        }}>
                                            {(index + 1).toString().padStart(2, '0')}
                                        </div>
                                    </div>
                                    <CardTitle style={{ margin: '0.5rem 0 0 0', fontSize: '1.2rem' }}>{item.title}</CardTitle>
                                </Card>
                            </>
                        )
                    })}
                </Grid>

                {visibleCount < shuffledPrompts.length && (
                    <LoadMoreButton onClick={handleLoadMore}>
                        Load More Prompts
                    </LoadMoreButton>
                )}
            </Section>

            <ModalOverlay $isOpen={!!selectedPrompt} onClick={handleCloseModal}>
                <ModalContent $isOpen={!!selectedPrompt} onClick={(e) => e.stopPropagation()}>
                    {selectedPrompt && (
                        <>
                            <CloseModalButton onClick={handleCloseModal}>
                                <FaTimes />
                            </CloseModalButton>
                            <ModalImage
                                src={`/prompts/${selectedPrompt.id}.png`}
                                alt={selectedPrompt.title}
                                $loaded={imgLoaded}
                                onLoad={() => setImgLoaded(true)}
                                onError={(e) => {
                                    if (e.target.src.includes('.png')) {
                                        e.target.src = `/prompts/${selectedPrompt.id}.jpg`;
                                    } else if (e.target.src.includes('.jpg')) {
                                        e.target.src = `/prompts/${selectedPrompt.id}.jpeg`;
                                    } else {
                                        e.target.style.display = 'none';
                                    }
                                }}
                            />
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                <CardNumber>PROMPT TITLE</CardNumber>
                                <CardTitle style={{ fontSize: '1.8rem', margin: 0 }}>{selectedPrompt.title}</CardTitle>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', background: 'rgba(0,0,0,0.03)', padding: '1.5rem', borderRadius: '16px' }}>
                                <CardNumber>DETAILED PROMPT</CardNumber>
                                <CardText style={{ fontSize: '1rem', lineHeight: '1.6', opacity: 1 }}>{selectedPrompt.prompt}</CardText>
                            </div>

                            <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                                <CopyButton onClick={handleCopy} style={{
                                    fontSize: '1rem',
                                    padding: '0.8rem 1.5rem',
                                    background: copied ? '#4caf50' : '#1a1a1a',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '12px',
                                    marginTop: 0,
                                    opacity: 1,
                                    flex: 1,
                                    justifyContent: 'center',
                                    transition: 'all 0.3s ease'
                                }}>
                                    <FaRegCopy />
                                    {copied ? 'Copied to Clipboard!' : 'Copy Prompt'}
                                </CopyButton>
                                {selectedPrompt.source && (
                                    <SourceLink href={selectedPrompt.source} target="_blank" rel="noopener noreferrer" style={{
                                        marginTop: 0,
                                        flex: 1,
                                        justifyContent: 'center',
                                        borderRadius: '12px',
                                        background: 'rgba(0,0,0,0.05)',
                                        border: '1px solid rgba(0,0,0,0.1)'
                                    }}>
                                        <FaExternalLinkAlt size="0.8em" />
                                        Source Link
                                    </SourceLink>
                                )}
                            </div>
                        </>
                    )}
                </ModalContent>
            </ModalOverlay>
        </>
    )
}
